/**
 * Add-on registry: per-property tracking add-ons sold on top of a plan.
 *
 * An add-on is a Stripe subscription ITEM on the organization's existing plan
 * subscription, carrying `metadata.propertyId` (and `metadata.addOnKey`). The
 * webhook seam (`provider/stripe/handlers/add-on-seam.ts`) turns item
 * create/update/delete into `Property.extraPromptPlatforms` changes, and the
 * nightly/day-1 dispatch gate in `@repo/core` does the rest.
 *
 * Pool size and cadence are recorded here and in item metadata but are NOT
 * enforced yet: an attached add-on enables the platform for every tracked
 * prompt on the property, daily. Enforcement belongs to the pools plan.
 *
 * Browser-safe (exported from `@repo/payments/client`): no imports.
 */

/**
 * Add-on platform identifiers. Duplicated from `ADD_ON_PROMPT_PLATFORMS` in
 * `packages/core/src/config/add-on-platforms.ts` rather than imported, because
 * `@repo/payments` must never import `@repo/core`. Same strings, same order
 * (the order is the canonical storage order for `extraPromptPlatforms`).
 */
export const ADD_ON_PLATFORMS = ["claude", "perplexity"] as const;

export type AddOnPlatform = (typeof ADD_ON_PLATFORMS)[number];

export type AddOnKey =
	| "perplexity50"
	| "perplexity250"
	| "perplexity600"
	| "claudeDaily"
	| "claudeWeekly";

/** Billing intervals as the admin UI and API name them. */
export type AddOnBillingInterval = "month" | "quarter" | "year";

export interface AddOnPrice {
	/** Stripe price id; undefined until the env var is set. */
	productId: string | undefined;
	interval: "month" | "year";
	intervalCount: number;
	amount: number;
	currency: "USD";
}

export interface AddOnDefinition {
	platform: AddOnPlatform;
	/** Prompts in the pool; null = set per contract (contact-only add-ons). */
	poolPrompts: number | null;
	cadence: "daily" | "weekly";
	/** Model pin, when the add-on is sold against a specific model. */
	model?: string;
	/**
	 * "self_serve": has prices and can be attached/purchased.
	 * "contact": talk to us; no price, never sold through checkout.
	 */
	sellable: "self_serve" | "contact";
	prices?: AddOnPrice[];
}

function perplexityPrices(
	envSuffix: "50" | "250" | "600",
	monthly: number,
	quarterly: number,
	yearly: number,
): AddOnPrice[] {
	// Full member expressions so Next.js can inline them in the browser.
	const ids = {
		"50": {
			month: process.env.NEXT_PUBLIC_PRICE_ID_ADDON_PERPLEXITY_50_MONTHLY,
			quarter:
				process.env.NEXT_PUBLIC_PRICE_ID_ADDON_PERPLEXITY_50_QUARTERLY,
			year: process.env.NEXT_PUBLIC_PRICE_ID_ADDON_PERPLEXITY_50_ANNUALLY,
		},
		"250": {
			month: process.env
				.NEXT_PUBLIC_PRICE_ID_ADDON_PERPLEXITY_250_MONTHLY,
			quarter:
				process.env.NEXT_PUBLIC_PRICE_ID_ADDON_PERPLEXITY_250_QUARTERLY,
			year: process.env
				.NEXT_PUBLIC_PRICE_ID_ADDON_PERPLEXITY_250_ANNUALLY,
		},
		"600": {
			month: process.env
				.NEXT_PUBLIC_PRICE_ID_ADDON_PERPLEXITY_600_MONTHLY,
			quarter:
				process.env.NEXT_PUBLIC_PRICE_ID_ADDON_PERPLEXITY_600_QUARTERLY,
			year: process.env
				.NEXT_PUBLIC_PRICE_ID_ADDON_PERPLEXITY_600_ANNUALLY,
		},
	}[envSuffix];
	return [
		{
			productId: ids.month,
			interval: "month",
			intervalCount: 1,
			amount: monthly,
			currency: "USD",
		},
		{
			productId: ids.quarter,
			interval: "month",
			intervalCount: 3,
			amount: quarterly,
			currency: "USD",
		},
		{
			productId: ids.year,
			interval: "year",
			intervalCount: 1,
			amount: yearly,
			currency: "USD",
		},
	];
}

export const ADD_ONS: Record<AddOnKey, AddOnDefinition> = {
	perplexity50: {
		platform: "perplexity",
		poolPrompts: 50,
		cadence: "daily",
		sellable: "self_serve",
		prices: perplexityPrices("50", 29, 78, 276),
	},
	perplexity250: {
		platform: "perplexity",
		poolPrompts: 250,
		cadence: "daily",
		sellable: "self_serve",
		prices: perplexityPrices("250", 99, 267, 948),
	},
	perplexity600: {
		platform: "perplexity",
		poolPrompts: 600,
		cadence: "daily",
		sellable: "self_serve",
		prices: perplexityPrices("600", 249, 672, 2388),
	},
	// Claude stays "talk to us" until Exp 009 reports. Both cadences are encoded
	// now so the pools plan can price the weekly SKU without a new concept.
	claudeDaily: {
		platform: "claude",
		poolPrompts: null,
		cadence: "daily",
		sellable: "contact",
	},
	claudeWeekly: {
		platform: "claude",
		poolPrompts: null,
		cadence: "weekly",
		sellable: "contact",
	},
};

export const ADD_ON_KEYS = Object.keys(ADD_ONS) as AddOnKey[];

export function isAddOnKey(value: string): value is AddOnKey {
	return Object.prototype.hasOwnProperty.call(ADD_ONS, value);
}

/** Map an API billing interval onto a price's Stripe interval + count. */
export function matchesBillingInterval(
	price: { interval: string; intervalCount?: number },
	interval: AddOnBillingInterval,
): boolean {
	const count = price.intervalCount ?? 1;
	if (interval === "year") return price.interval === "year" && count === 1;
	if (interval === "quarter")
		return price.interval === "month" && count === 3;
	return price.interval === "month" && count === 1;
}

/** The add-on's price for a billing interval, if it has one. */
export function getAddOnPrice(
	addOnKey: AddOnKey,
	interval: AddOnBillingInterval,
): AddOnPrice | undefined {
	return ADD_ONS[addOnKey].prices?.find((price) =>
		matchesBillingInterval(price, interval),
	);
}

/** Resolve a Stripe price id to its add-on, or undefined if it isn't one. */
export function findAddOnByPriceId(priceId: string | null | undefined):
	| {
			key: AddOnKey;
			addOn: AddOnDefinition;
			price: AddOnPrice;
	  }
	| undefined {
	if (!priceId) return undefined;
	for (const key of ADD_ON_KEYS) {
		const addOn = ADD_ONS[key];
		const price = addOn.prices?.find((p) => p.productId === priceId);
		if (price) return { key, addOn, price };
	}
	return undefined;
}
